import {
  PencilIcon,
  BellPlusIcon,
  OneTimeArrowIcon,
  MapExpandIcon,
  UploadSmallIcon,
  CopyIcon,
  ArchiveIcon,
  ProductsIcon,
} from '@/assets/icons/icons';
import { ClearAll } from '@/assets/images/ClearAll';
import { CheckCircle } from '@/assets/images/CheckCircle';
import type { ShipmentHeader } from '../../types';
import type { VitalModalType } from '../../../partials/sensorVitals';

export type ShipmentTableActionKey =
  | 'open_comments'
  | 'edit_shipment'
  | 'setup_notification'
  | 'one_time_message'
  | 'map_full_screen'
  | 'share_tracking_link'
  | 'dismiss_alerts'
  | 'mark_as_delivered'
  | 'export_coc'
  | 'product_list'
  | 'archive_shipment'
  | 'schedule_pickup';

export const MENU_ITEMS: Array<{
  key: ShipmentTableActionKey;
  label: string;
  danger?: boolean;
  expand?: boolean;
  icon: React.ReactNode;
}> = [
  {
    key: 'edit_shipment',
    label: 'Edit',
    icon: <PencilIcon color='var(--text-primary)' />,
  },
  {
    key: 'setup_notification',
    label: 'Add notification',
    icon: <BellPlusIcon />,
  },
  {
    key: 'one_time_message',
    label: 'One time message',
    icon: <OneTimeArrowIcon />,
  },
  {
    key: 'map_full_screen',
    label: 'Map full screen',
    icon: <MapExpandIcon />,
  },
  {
    key: 'share_tracking_link',
    label: 'Share tracking link',
    icon: (
      <UploadSmallIcon color='var(--text-primary)' width={16} height={16} />
    ),
  },
  { key: 'dismiss_alerts', label: 'Dismiss alerts', icon: <ClearAll /> },
  {
    key: 'mark_as_delivered',
    label: 'Mark as delivered',
    icon: <CheckCircle />,
  },
  {
    key: 'export_coc',
    label: 'Export chain of custody',
    icon: <CopyIcon />,
    expand: true,
  },
  {
    key: 'product_list',
    label: 'Product list',
    icon: <ProductsIcon color='var(--text-primary)' />,
  },
  {
    key: 'archive_shipment',
    label: 'Archive shipment',
    danger: true,
    icon: <ArchiveIcon width={16} height={16} />,
  },
];

export const COLUMN_WIDTHS: Record<string, number> = {
  reference_number: 200,
  custom_status: 260,
  from_location: 160,
  to_location: 160,
  products: 140,
  courier_service_level: 140,
};

export const DEFAULT_COLUMN_WIDTH = 170;
export const ACTION_COLUMN_WIDTH = 120;
export const TABLE_BOTTOM_OFFSET = 0;
export const TABLE_HEADER_FALLBACK_HEIGHT = 28;
export const MIN_TABLE_BODY_HEIGHT = 380;
export const DEFAULT_PAGE_SIZE = 300;
export const DEFAULT_ROW_HEIGHT = 88;
export const STICKY_COLUMN_MIN_WIDTH = 1024;
export const GRID_LOAD_MORE_OFFSET_PX = 180;
export const INITIAL_SKELETON_ROW_COUNT = 8;
export const NOTIFICATION_ENABLED = 'enabled';
export const NOTIFICATION_DISABLED = 'disabled';
export const NOTIFICATION_MUTED = 'muted';
export const PLACEHOLDER_ROW_KEY = '__shipment_placeholder_key';

export const RICH_CELL_KEYS = new Set([
  'reference_number',
  'custom_status',
  'from_location',
  'to_location',
  'products',
  'teams',
  'alert_count',
]);

export const SENSOR_COLUMN_VITAL_TYPES: Record<string, VitalModalType> = {
  sensor_temperature: 'temperature',
  sensor_battery: 'battery',
  sensor_humidity: 'humidity',
  sensor_shock: 'shock_value',
  sensor_light: 'light',
  sensor_pressure: 'pressure',
};

/**
 * ShipmentAlert.alert_type that represents a confirmed (not pre-alarm/
 * nearing-limit) excursion for each vital, keyed by VitalModalType.
 */
export const VITAL_EXCURSION_ALERT_TYPES: Partial<
  Record<VitalModalType, string>
> = {
  temperature: 'temperature_excursion_detected',
  humidity: 'excessive_humidity_detected',
  pressure: 'excessive_pressure_detected',
  battery: 'low_battery',
  light: 'excessive_light_exposure_detected',
  shock_value: 'excessive_shock_detected',
};

export const CORE_COLUMN_KEYS = new Set([
  'reference_number',
  'custom_status',
  'from_location',
  'to_location',
  'products',
  'courier_service_level',
]);

export const SKELETON_HEADERS: ShipmentHeader[] = [
  { key: 'reference_number', value: 'ID' },
  { key: 'custom_status', value: 'Tracking' },
  { key: 'from_location', value: 'Origin' },
  { key: 'to_location', value: 'Destination' },
  { key: 'products', value: 'Products' },
  { key: 'courier_service_level', value: 'Service level' },
];
